/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getOffset from '@stdlib/ndarray/base/offset';
import getData from '@stdlib/ndarray/base/data-buffer';
import ndarraylike2scalar from '@stdlib/ndarray/base/ndarraylike2scalar';
import { ndarray as strided } from './../../../../strided/ztest2';


// MAIN //

/**
* Computes a two-sample Z-test for two one-dimensional ndarrays.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   first one-dimensional input ndarray.
*     -   second one-dimensional input ndarray.
*     -   a zero-dimensional output ndarray containing a results object.
*     -   a zero-dimensional ndarray specifying the alternative hypothesis.
*     -   a zero-dimensional ndarray specifying the significance level.
*     -   a zero-dimensional ndarray specifying the difference in means under the null hypothesis.
*     -   a zero-dimensional ndarray specifying the known standard deviation of the first one-dimensional input ndarray.
*     -   a zero-dimensional ndarray specifying the known standard deviation of the second one-dimensional input ndarray.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @returns {ndarrayLike} output ndarray
*
* @example
* import Float64Results from '@stdlib/stats/base/ztest/two-sample/results/float64';
* import resolveEnum from '@stdlib/stats/base/ztest/alternative-resolve-enum';
* import structFactory from '@stdlib/array/struct-factory';
* import scalar2ndarray from '@stdlib/ndarray/from-scalar';
* import vector from '@stdlib/ndarray/vector/ctor';
* import ndarray from '@stdlib/ndarray/ctor';
*
* var opts = {
*     'dtype': 'generic'
* };
*
* // Define one-dimensional input ndarrays:
* var x = vector( [ 4.0, 4.0, 6.0, 6.0, 5.0 ], 'generic' );
* var y = vector( [ 3.0, 3.0, 5.0, 7.0, 7.0 ], 'generic' );
*
* // Specify the alternative hypothesis:
* var alt = scalar2ndarray( resolveEnum( 'two-sided' ), {
*     'dtype': 'int8'
* });
*
* // Specify the significance level:
* var alpha = scalar2ndarray( 0.05, opts );
*
* // Specify the difference in means under the null hypothesis:
* var diff = scalar2ndarray( 0.0, opts );
*
* // Specify the known standard deviations:
* var sigmax = scalar2ndarray( 1.0, opts );
* var sigmay = scalar2ndarray( 2.0, opts );
*
* // Create a zero-dimensional results ndarray:
* var ResultsArray = structFactory( Float64Results );
* var out = new ndarray( Float64Results, new ResultsArray( 1 ), [], [ 0 ], 0, 'row-major' );
*
* // Perform a Z-test:
* var v = ztest2( [ x, y, out, alt, alpha, diff, sigmax, sigmay ] );
* // returns <ResultsArray>
*
* console.log( v.get().toString() );
*/
function ztest2( arrays ) {
	var sigmax;
	var sigmay;
	var alpha;
	var diff;
	var alt;
	var out;
	var x;
	var y;

	x = arrays[ 0 ];
	y = arrays[ 1 ];
	out = ndarraylike2scalar( arrays[ 2 ] );

	alt = ndarraylike2scalar( arrays[ 3 ] );
	alpha = ndarraylike2scalar( arrays[ 4 ] );
	diff = ndarraylike2scalar( arrays[ 5 ] );
	sigmax = ndarraylike2scalar( arrays[ 6 ] );
	sigmay = ndarraylike2scalar( arrays[ 7 ] );

	strided( numelDimension( x, 0 ), numelDimension( y, 0 ), alt, alpha, diff, sigmax, getData( x ), getStride( x, 0 ), getOffset( x ), sigmay, getData( y ), getStride( y, 0 ), getOffset( y ), out ); // eslint-disable-line max-len

	return arrays[ 2 ];
}


// EXPORTS //

export default ztest2;
